import type { Guide } from "./types.js";
import { Lead, H2, P, Ul, Li, AppLink, C, Callout } from "./primitives.js";

export const meta = {
  id: "error-log",
  title: "Errors page — central failure + warning log",
  category: "Reference",
  lastUpdated: "2026-06-12",
  description: "Every API failure lands in pipeline_errors via a global hook. The Errors page surfaces them with a ready-to-paste Claude fix prompt so you can see what broke and get a fix fast.",
  relatedGuides: ["troubleshooting", "produce-article", "asset-storage"],
  readTimeMin: 4,
} as const;

export function Body() {
  return (
    <>
      <Lead>
        The <AppLink to="errors">Errors</AppLink> page is the central place to see what failed across the pipeline. Every catch block writes a row. Every orchestrator that returns a non-empty <C>warnings: string[]</C> array writes one row per warning. Polls every 30s.
      </Lead>

      <H2>What gets logged</H2>
      <Ul>
        <Li><strong>level: error</strong> — the operation threw. Nothing useful was returned. The row carries the error message + full stack + the request context (URL, body snippet).</Li>
        <Li><strong>level: warning</strong> — the operation completed but reported a non-fatal issue. Example: Sanity upload succeeded but Drive failed (asset row is <C>partial</C>). Example: scene 7's image-gen timed out but the article still produced.</Li>
        <Li><strong>level: info</strong> — reserved for future "notable success" entries. Not emitted by anything today.</Li>
      </Ul>

      <Callout kind="tip">
        Warnings are usually the early-warning signal for a config that's about to break in production. A handful of "Drive: refresh-token exchange failed" warnings means your Google OAuth token expired and the next viral run will fail outright. Watch the warning count.
      </Callout>

      <H2>What gets instrumented</H2>
      <P>
        <strong>Everything.</strong> A global <C>onSend</C> hook in the ui-server logs <em>every</em> 5xx (and 400) API response to <C>pipeline_errors</C>, regardless of which route produced it — so you never have to wonder whether a given endpoint "remembered" to log. Operation is the route (<C>POST /api/clusters/:id/expand</C>); keyword + cluster ids are lifted from the route automatically. On top of that, orchestrators still emit rich domain entries (e.g. <C>scheduler.gsc-sync</C>, <C>viral.scene_image[3]</C>) and per-warning rows from <C>warnings: string[]</C>.
      </P>

      <H2>Fix with Claude</H2>
      <P>
        Expand any error row and you'll find a <strong>"Fix with Claude"</strong> box with a generated, copy-able prompt. It bundles the operation, message, stack, context, and a map of the likely source files, then asks Claude to find the root cause and give the exact fix. Click <strong>Copy prompt</strong>, paste it into Claude Code (or the in-app chat), and you get a diagnosis + patch — no manual triage.
      </P>

      <H2>Page layout</H2>
      <Ul>
        <Li><strong>KPI tiles</strong> — unresolved errors, unresolved warnings, lifetime resolved.</Li>
        <Li><strong>"Noisiest operations · last 24h"</strong> — top operations by unresolved-count. Click any chip to filter the table by that operation prefix.</Li>
        <Li><strong>Filters</strong> — unresolved-only toggle, level dropdown, operation-prefix search, keyword-id filter.</Li>
        <Li><strong>Table</strong> — timestamp, level pill, operation namespace, keyword link, truncated message, status pill, Resolve button. Click any row to expand its full stack + context JSON.</Li>
        <Li><strong>Sidebar badge</strong> — red number on the Errors entry showing unresolved errors only (warnings don't trigger it).</Li>
      </Ul>

      <H2>Operation namespace</H2>
      <P>
        Operations are dotted strings — pipeline-section first, then sub-operation. This makes filtering tractable as the surface grows. Examples:
      </P>
      <Ul>
        <Li><C>pipeline.brief</C></Li>
        <Li><C>pipeline.voiceover.scenes</C></Li>
        <Li><C>distribute.tiktok</C></Li>
        <Li><C>asset.upload.sanity</C> (when shipped for asset-level errors)</Li>
        <Li><C>viral.scene_image[3]</C> (per-scene fan-out errors in the viral orchestrator)</Li>
      </Ul>
      <P>
        The operation-prefix filter is a startswith match, so <C>distribute.</C> shows you every channel; <C>pipeline.viral</C> shows you only the viral surface.
      </P>

      <H2>Workflow</H2>
      <Ul>
        <Li>Open the Errors page at the start of the day. Scan KPI tiles.</Li>
        <Li>If anything's red: click the noisiest-operations chip to filter, expand the first row, read the stack.</Li>
        <Li>Fix the root cause (e.g. regenerate the ElevenLabs key, top up Replicate credits, fix a stale Drive folder ID).</Li>
        <Li>Click <C>Resolve</C> on each row that's now stale. Resolved rows stay in the table (toggle "Unresolved only" off to see them) but stop counting toward the sidebar badge.</Li>
        <Li>For systematic problems: filter by operation prefix → spot patterns ("storyboard fails 30% of the time when the article is &lt;800 words" → tighten the Sonnet prompt's word-count constraint).</Li>
      </Ul>

      <H2>Failure modes of the logger itself</H2>
      <Ul>
        <Li>
          <strong>"Persist failed" line in ui-server stderr</strong> — the SQLite write itself errored. Last-ditch: error is written to stderr so it isn't completely silent. Investigate the DB file (locked, full, permissions).
        </Li>
        <Li>
          <strong>Context column truncated</strong> — a single row's context JSON is capped at ~8KB. The row carries <C>_truncated: true</C> + the original byte count. If you need the full payload, check the ui-server log around the timestamp.
        </Li>
        <Li>
          <strong>No rows after a known failure</strong> — only catch blocks the logger touches are instrumented. If something fails further down (deep inside an orchestrator) it won't appear here unless the orchestrator either rethrew (caught by the route's catch) or returned a warning string.
        </Li>
      </Ul>
    </>
  );
}

export const guide: Guide = { meta, Body };
